import PropTypes from 'prop-types';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faCircle,
  faPen,
  faPlus,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { KeyStatus } from './translations';
import TranslationRowEditor from './TranslationRowEditor';

/** How many language chips fit next to a row before they get summarised. */
const LANGUAGE_CHIPS = 4;

/** Marks the search term inside a text so it can be spotted in a long list. */
const Highlight = ({ text, term }) => {
  if (!term) return text;
  const index = text.toLowerCase().indexOf(term.toLowerCase());
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark>{text.slice(index, index + term.length)}</mark>
      {text.slice(index + term.length)}
    </>
  );
};

Highlight.propTypes = {
  text: PropTypes.string.isRequired,
  term: PropTypes.string,
};

/** Section prefix stays readable but steps back – the leaf is what differs. */
const KeyLabel = ({ path, term }) => {
  const separator = path.lastIndexOf('.');
  if (separator === -1) return <Highlight text={path} term={term} />;
  return (
    <>
      <span className="TranslationEditor-keyPrefix">
        <Highlight text={path.slice(0, separator + 1)} term={term} />
      </span>
      <span>
        <Highlight text={path.slice(separator + 1)} term={term} />
      </span>
    </>
  );
};

KeyLabel.propTypes = {
  path: PropTypes.string.isRequired,
  term: PropTypes.string,
};

const TranslationRow = ({
  entry,
  language,
  className,
  searchTerm = '',
  expanded = false,
  focusOnOpen = false,
  showLanguages = false,
  languageStates = null,
  coupledLanguages = null,
  onToggle,
  onAdopt,
  onCreateEmpty,
  onChange,
  onReset,
  onSelectLanguage,
}) => {
  const {
    path,
    mainValue,
    mainLanguage,
    hasMain,
    overrideValue,
    hasOverride,
    isNew,
    isDirty,
    status,
    placeholders,
    otherLanguages = [],
  } = entry;

  const hasPlaceholderIssue = Boolean(
    placeholders.missing.length || placeholders.added.length,
  );
  const isBroken =
    status === KeyStatus.EMPTY ||
    status === KeyStatus.UNKNOWN ||
    hasPlaceholderIssue;

  /**
   * Only languages that deviate from this row are worth a chip: one that has no
   * override while this language does (or the other way round), plus languages
   * with unsaved edits. Where all languages agree the column stays empty.
   */
  const deviating = otherLanguages.filter(
    (other) => other.has !== hasOverride || other.dirty,
  );

  const flagTitle = () => {
    if (status === KeyStatus.UNKNOWN) return 'Key gibt es im Hauptfile nicht';
    if (status === KeyStatus.EMPTY)
      return 'Leerer Override blendet den Text aus';
    if (hasPlaceholderIssue) return 'Platzhalter passt nicht zum Hauptext';
    return '';
  };

  return (
    <div
      className={`TranslationEditor-row status-${status} ${
        isDirty ? 'dirty' : ''
      } ${expanded ? 'expanded' : ''}`}
    >
      <button
        type="button"
        className="TranslationEditor-cellKey"
        onClick={() => onToggle(path)}
        aria-expanded={expanded}
        aria-controls={`editor-${path}`}
        title={path}
      >
        <FontAwesomeIcon
          className="TranslationEditor-caret"
          icon={expanded ? faChevronDown : faChevronRight}
        />
        <code>
          <KeyLabel path={path} term={searchTerm} />
        </code>
      </button>

      <button
        type="button"
        className="TranslationEditor-cellMain"
        onClick={() => onToggle(path)}
        aria-label={`${path} bearbeiten`}
        tabIndex={-1}
      >
        {hasMain && mainValue !== '' ? (
          <>
            {mainLanguage !== language && (
              <span
                className="TranslationEditor-fallbackTag"
                title={`fehlt in ${language}, angezeigt wird ${mainLanguage}`}
              >
                {mainLanguage}
              </span>
            )}
            <Highlight text={mainValue} term={searchTerm} />
          </>
        ) : (
          <span className="TranslationEditor-muted">
            {hasMain ? 'im Hauptfile leer' : 'nicht im Hauptfile'}
          </span>
        )}
      </button>

      {hasOverride ? (
        <button
          type="button"
          className="TranslationEditor-cellOverride"
          onClick={() => onToggle(path, true)}
          aria-label={`Override für ${path} bearbeiten`}
        >
          {overrideValue === '' ? (
            <span className="TranslationEditor-muted">(leer)</span>
          ) : (
            <Highlight text={overrideValue} term={searchTerm} />
          )}
        </button>
      ) : (
        <button
          type="button"
          className="TranslationEditor-cellAdopt"
          onClick={() => onAdopt(path, true)}
          disabled={!hasMain}
          aria-label={`Key ${path} mit Haupttext nach ${className} übernehmen`}
          title={
            coupledLanguages
              ? `Key in ${coupledLanguages.join(' · ')} anlegen (Keys gekoppelt)`
              : `Key mit Haupttext nach ${className} · ${language} anlegen`
          }
        >
          <FontAwesomeIcon icon={faPlus} />
          <span>übernehmen</span>
        </button>
      )}

      {showLanguages && (
        <div className="TranslationEditor-cellLanguages">
          {deviating.slice(0, LANGUAGE_CHIPS).map(({ code, has, dirty }) => (
            <button
              type="button"
              key={code}
              className={`TranslationEditor-langChip ${has ? 'has' : ''} ${
                dirty ? 'dirty' : ''
              }`}
              onClick={() => onSelectLanguage(code)}
              title={
                has
                  ? `${code} überschreibt diesen Key ebenfalls${
                      dirty ? ' (ungespeichert)' : ''
                    } – zu ${code} wechseln`
                  : `kein Override in ${code} – zu ${code} wechseln`
              }
            >
              {code}
            </button>
          ))}
          {deviating.length > LANGUAGE_CHIPS && (
            <span
              className="TranslationEditor-langMore"
              title={deviating
                .slice(LANGUAGE_CHIPS)
                .map(
                  ({ code, has }) =>
                    `${code}: ${has ? 'Override' : 'kein Override'}`,
                )
                .join(', ')}
            >
              +{deviating.length - LANGUAGE_CHIPS}
            </span>
          )}
        </div>
      )}

      <div className="TranslationEditor-cellFlags">
        {isDirty && (
          <FontAwesomeIcon
            className="flag dirty"
            icon={faPen}
            title={isNew ? 'neu, noch nicht im Repo' : 'geändert'}
          />
        )}
        {isBroken && (
          <FontAwesomeIcon
            className="flag broken"
            icon={faTriangleExclamation}
            title={flagTitle()}
          />
        )}
        {status === KeyStatus.IDENTICAL && !isNew && (
          <FontAwesomeIcon
            className="flag identical"
            icon={faCircle}
            title="Gleicher Text wie im Hauptfile – kann entfernt werden"
          />
        )}
      </div>

      {expanded && (
        <div id={`editor-${path}`} className="TranslationEditor-editorWrapper">
          <TranslationRowEditor
            path={path}
            languageStates={languageStates || []}
            language={language}
            className={className}
            focusOnOpen={focusOnOpen}
            onAdopt={onAdopt}
            onCreateEmpty={onCreateEmpty}
            onChange={onChange}
            onReset={onReset}
          />
        </div>
      )}
    </div>
  );
};

TranslationRow.propTypes = {
  entry: PropTypes.shape({
    path: PropTypes.string.isRequired,
    mainValue: PropTypes.string.isRequired,
    mainLanguage: PropTypes.string.isRequired,
    hasMain: PropTypes.bool.isRequired,
    overrideValue: PropTypes.string,
    hasOverride: PropTypes.bool.isRequired,
    baseValue: PropTypes.string,
    isNew: PropTypes.bool,
    isDirty: PropTypes.bool,
    status: PropTypes.string.isRequired,
    placeholders: PropTypes.shape({
      missing: PropTypes.arrayOf(PropTypes.string).isRequired,
      added: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    otherLanguages: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string.isRequired,
        has: PropTypes.bool,
        dirty: PropTypes.bool,
      }),
    ),
  }).isRequired,
  language: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  searchTerm: PropTypes.string,
  expanded: PropTypes.bool,
  focusOnOpen: PropTypes.bool,
  showLanguages: PropTypes.bool,
  languageStates: PropTypes.array,
  coupledLanguages: PropTypes.arrayOf(PropTypes.string),
  onToggle: PropTypes.func.isRequired,
  onAdopt: PropTypes.func.isRequired,
  onCreateEmpty: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onSelectLanguage: PropTypes.func.isRequired,
};

export default React.memo(TranslationRow);
